import type { Database } from './database';

export type SMSTemplate = Database['public']['Tables']['sms_templates']['Row'];
export type SMSTemplateInsert = Database['public']['Tables']['sms_templates']['Insert'];
export type SMSTemplateUpdate = Database['public']['Tables']['sms_templates']['Update'];

export type SMSSentLog = Database['public']['Tables']['sms_sent_log']['Row'];
export type SMSSentLogInsert = Database['public']['Tables']['sms_sent_log']['Insert'];

export type CaseDispatchRule = Database['public']['Tables']['case_dispatch_rules']['Row'];
export type CaseDispatchRuleInsert = Database['public']['Tables']['case_dispatch_rules']['Insert'];
export type CaseDispatchRuleUpdate = Database['public']['Tables']['case_dispatch_rules']['Update'];

export type SMSCategory = SMSTemplate['category'];
export type SMSLanguage = SMSTemplate['language'];
export type SMSDeliveryStatus = SMSSentLog['delivery_status'];

// SMS template with variable values for sending
export interface SMSTemplateWithValues extends SMSTemplate {
  variable_values?: Record<string, string>;
}

// SMS sending data
export interface SendSMSData {
  customer_user_id: string;
  phone_number: string;
  template_id: string;
  variable_values: Record<string, string>;
  case_id?: string;
}

// SMS log filters
export interface SMSLogFilters {
  staff_user_id?: string;
  customer_user_id?: string;
  delivery_status?: SMSDeliveryStatus;
  date_from?: string;
  date_to?: string;
}

// Helper functions
export const replaceSMSVariables = (
  template: string,
  variables: Record<string, string>
): string => {
  let result = template;
  Object.entries(variables).forEach(([key, value]) => {
    result = result.replace(new RegExp(`{{${key}}}`, 'g'), value);
  });
  return result;
};

export const extractSMSVariables = (template: string): string[] => {
  const regex = /{{(\w+)}}/g;
  const matches = [];
  let match;
  while ((match = regex.exec(template)) !== null) {
    matches.push(match[1]);
  }
  return [...new Set(matches)];
};

export const calculateSMSSegments = (message: string): number => {
  const length = message.length;
  if (length === 0) return 0;
  if (length <= 160) return 1;
  return Math.ceil(length / 153); // 153 chars per segment for concatenated messages
};

export const estimateSMSCost = (segments: number): number => {
  const costPerSegmentAED = 0.05; // Approximate cost in AED
  return segments * costPerSegmentAED;
};

export const getSMSCategoryLabel = (category: SMSCategory): string => {
  const labels: Record<SMSCategory, string> = {
    payment: 'Payment',
    security: 'Security',
    kyc: 'KYC',
    general: 'General',
    account: 'Account',
    card: 'Card',
  };
  return labels[category];
};

export const getSMSDeliveryStatusLabel = (status: SMSDeliveryStatus): string => {
  const labels: Record<SMSDeliveryStatus, string> = {
    pending: 'Pending',
    queued: 'Queued',
    sent: 'Sent',
    delivered: 'Delivered',
    failed: 'Failed',
    undelivered: 'Undelivered',
  };
  return labels[status];
};

export const getSMSDeliveryStatusColor = (status: SMSDeliveryStatus): string => {
  const colors: Record<SMSDeliveryStatus, string> = {
    pending: 'gray',
    queued: 'blue',
    sent: 'blue',
    delivered: 'green',
    failed: 'red',
    undelivered: 'red',
  };
  return colors[status];
};

